import { useState, useRef, useEffect, useCallback } from "react";

const FONTS = ["Arial", "Times New Roman", "Calibri", "Georgia", "Verdana", "Courier New", "Trebuchet MS", "Comic Sans MS", "Garamond", "Palatino", "Tahoma", "Cambria"];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];
const LINE_HEIGHTS = [
  { label: "1.0", value: "1" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "2.0", value: "2" },
  { label: "2.5", value: "2.5" },
  { label: "3.0", value: "3" },
];
const PARAGRAPH_STYLES = [
  { label: "Normal", tag: "P", style: "font-size:12pt;font-weight:normal;" },
  { label: "Title", tag: "H1", style: "font-size:28pt;font-weight:700;" },
  { label: "Heading 1", tag: "H1", style: "font-size:22pt;font-weight:700;" },
  { label: "Heading 2", tag: "H2", style: "font-size:18pt;font-weight:700;" },
  { label: "Heading 3", tag: "H3", style: "font-size:14pt;font-weight:700;" },
  { label: "Subtitle", tag: "H4", style: "font-size:13pt;font-style:italic;font-weight:400;" },
  { label: "Quote", tag: "BLOCKQUOTE", style: "font-size:12pt;font-style:italic;border-left:3px solid #9ca3af;padding-left:12px;color:#4b5563;" },
];
const SPECIAL_CHARS = ["©", "®", "™", "§", "¶", "•", "–", "—", "…", "°", "±", "×", "÷", "¼", "½", "¾", "α", "β", "π", "Σ", "€", "£", "¥", "✓", "★", "→", "←", "↔"];

const PX_PER_IN = 96;
const PAGE_SIZE_MAP = {
  pocket_book: { width: 4.25, height: 6.87 },
  pocket: { width: 4.25, height: 6.87 },
  digest: { width: 5.5, height: 8.5 },
  trade: { width: 6.0, height: 9.0 },
  letter: { width: 8.5, height: 11.0 },
  a4: { width: 8.27, height: 11.69 },
  a5: { width: 5.83, height: 8.27 },
};
const MARGIN_IN_MAP = { narrow: 0.5, normal: 0.8, wide: 1.2 };
const THEME_MAP = {
  white: { bg: "#ffffff", text: "#1a1a1a" },
  cream: { bg: "#fdfbf7", text: "#2d251e" },
  sepia: { bg: "#f4ecd8", text: "#433422" },
  charcoal: { bg: "#1b1b1b", text: "#f5f5f5" },
};


const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

// ── Page border / decorative page-design templates ─────────────────────────
export const PAGE_BORDER_STYLES = [
  { id: "none", label: "No Border", accent: "#9ca3af" },
  { id: "classic_frame", label: "Classic Double Frame", accent: "#2d251e" },
  { id: "chevron_ribbon", label: "Chevron & Ribbon (Teal/Orange)", accent: "#0e3a5c" },
  { id: "mint_corners", label: "Mint Triangle Corners", accent: "#7fb89a" },
  { id: "stepped_corner", label: "Stepped Corner Bars", accent: "#9bcab0" },
  { id: "olive_band", label: "Olive Chapter Band", accent: "#3c4a32" },
  { id: "sidebar_rule", label: "Rule & Sidebar Accent", accent: "#c0394b" },
];

// Decorative space each template reserves (px) so content/header/footer never collide with it.
const DECOR_EXTRA_PX = {
  none: { top: 0, bottom: 0, left: 0 },
  classic_frame: { top: 0, bottom: 0, left: 0 },
  chevron_ribbon: { top: 40, bottom: 32, left: 0 },
  mint_corners: { top: 0, bottom: 0, left: 0 },
  stepped_corner: { top: 14, bottom: 0, left: 0 },
  olive_band: { top: 56, bottom: 0, left: 0 },
  sidebar_rule: { top: 30, bottom: 0, left: 16 },
};
const TEMPLATE_HAS_OWN_HEADER = ["chevron_ribbon", "olive_band", "sidebar_rule"];
const TEMPLATE_HAS_OWN_FOOTER = ["chevron_ribbon"];

function PageBorderDecor({ styleId, themeColors, headerText, footerText, pageNumber = 1 }) {
  if (!styleId || styleId === "none") return null;

  if (styleId === "classic_frame") {
    return (
      <div
        className="absolute inset-[6px] pointer-events-none z-0"
        style={{ border: `1px solid ${themeColors.text}`, outline: `3px double ${themeColors.text}`, outlineOffset: "-6px" }}
      />
    );
  }

  if (styleId === "chevron_ribbon") {
    return (
      <>
        <div className="absolute top-0 left-0 right-0 h-[34px] pointer-events-none z-0 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg,#e8821c 0%,#e8821c 38%,#0e3a5c 38%,#0e3a5c 100%)", clipPath: "polygon(0 0,100% 0,100% 55%,0 100%)" }} />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[26px] pointer-events-none z-0 flex items-center justify-between px-3" style={{ background: "linear-gradient(90deg,#0e3a5c,#1e6e4a)" }}>
          <span className="text-white text-[8px] font-bold tracking-wide opacity-90">{footerText || "From Hope to Healing"}</span>
          <span className="text-white text-[8px] font-extrabold">{pageNumber}</span>
        </div>
      </>
    );
  }

  if (styleId === "mint_corners") {
    return (
      <>
        <div className="absolute top-0 right-0 w-0 h-0 pointer-events-none z-0" style={{ borderTop: "60px solid #bfe3cf", borderLeft: "60px solid transparent" }} />
        <div className="absolute top-0 right-0 w-[5px] h-full pointer-events-none z-0" style={{ background: "#bfe3cf" }} />
        <div className="absolute bottom-0 left-0 w-0 h-0 pointer-events-none z-0" style={{ borderBottom: "60px solid #bfe3cf", borderRight: "60px solid transparent" }} />
      </>
    );
  }

  if (styleId === "stepped_corner") {
    return (
      <>
        <div className="absolute top-0 left-0 pointer-events-none z-0" style={{ width: "70px", height: "70px", background: "linear-gradient(135deg,#a9d6bd 0%,#a9d6bd 50%,transparent 50%)" }} />
        <div className="absolute top-3 left-3 pointer-events-none z-0" style={{ width: "46px", height: "46px", background: "linear-gradient(135deg,#cfeada 0%,#cfeada 50%,transparent 50%)" }} />
        <div className="absolute bottom-0 left-0 pointer-events-none z-0" style={{ width: "55px", height: "55px", background: "linear-gradient(45deg,#a9d6bd 0%,#a9d6bd 50%,transparent 50%)" }} />
      </>
    );
  }

  if (styleId === "olive_band") {
    return (
      <div className="absolute top-0 left-0 right-0 pointer-events-none z-0 px-5 py-3" style={{ background: "#3c4a32" }}>
        <div className="text-white text-[7px] font-bold tracking-[0.25em] uppercase opacity-80">{headerText ? "Chapter" : ""}</div>
        <div className="text-white text-[13px] font-extrabold leading-tight">{headerText || "Document Title"}</div>
      </div>
    );
  }

  if (styleId === "sidebar_rule") {
    return (
      <>
        <div className="absolute top-0 bottom-0 left-0 w-[5px] pointer-events-none z-0" style={{ background: "#c0394b" }} />
        <div className="absolute top-0 left-[18px] right-3 pointer-events-none z-0 border-b border-gray-300 pt-2 pb-1.5 text-[8px] text-gray-500 italic">
          {headerText || "Document Title"}
        </div>
      </>
    );
  }

  return null;
}

// ── Toolbar button ─────────────────────────────────────────────────────────
function ToolBtn({ title, active, disabled, onClick, children, className = "" }) {
  return (
    <button
      title={title}
      disabled={disabled}
      onMouseDown={(e) => { e.preventDefault(); onClick && onClick(); }}
      className={`px-[7px] py-[4px] border rounded-[3px] text-[13px] inline-flex items-center gap-[3px] whitespace-nowrap transition-colors duration-150 ${disabled
          ? "cursor-not-allowed text-gray-300 border-transparent bg-transparent"
          : active
            ? "border-[#0078d4] bg-[#dbeafe] text-[#1a1a1a] cursor-pointer"
            : "border-transparent bg-transparent hover:bg-gray-100 text-[#1a1a1a] cursor-pointer"
        } ${className}`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-[1px] h-6 bg-gray-300 mx-1 self-center shrink-0" />;
}

// ── Color picker inline ────────────────────────────────────────────────────
const COLORS = [
  "#000000", "#1a1a1a", "#374151", "#6b7280", "#9ca3af", "#d1d5db", "#ffffff",
  "#dc2626", "#ea580c", "#d97706", "#16a34a", "#0891b2", "#2563eb", "#7c3aed",
  "#fca5a5", "#fdba74", "#fde68a", "#86efac", "#67e8f9", "#93c5fd", "#c4b5fd",
];
function ColorPicker({ label, onColor, currentColor, icon }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div ref={ref} className="relative inline-block">
      <button
        title={label}
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className="px-1.5 py-1 border border-transparent rounded-[3px] bg-transparent hover:bg-gray-100 cursor-pointer text-[13px] flex flex-col items-center gap-[1px]"
      >
        <span className="text-xs leading-none">{icon}</span>
        <span style={{ background: currentColor }} className="w-4 h-[3px] rounded-[1px]" />
      </button>
      {open && (
        <div className="absolute top-[110%] left-0 z-[999] bg-white border border-gray-300 rounded-md p-2 grid grid-cols-7 gap-1 shadow-lg w-[182px]">
          {COLORS.map((c) => (
            <button
              key={c}
              onMouseDown={(e) => { e.preventDefault(); onColor(c); setOpen(false); }}
              style={{ background: c }}
              className={`w-5 h-5 rounded-[3px] cursor-pointer ${c === currentColor ? "border-2 border-[#2563eb]" : "border border-gray-300"
                }`}
            />
          ))}
          <input
            type="color"
            value={currentColor}
            onChange={(e) => onColor(e.target.value)}
            className="col-span-7 w-full h-6 cursor-pointer border-none p-0 bg-transparent"
          />
        </div>
      )}
    </div>
  );
}

// ── Dropdown menu helper ────────────────────────────────────────────────────
function DropdownMenu({ label, title, children, width = "w-44" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div ref={ref} className="relative inline-block">
      <button
        title={title}
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        className="px-2 py-1 border border-gray-300 rounded-[3px] bg-white hover:bg-gray-50 text-[11px] cursor-pointer flex items-center gap-1"
      >
        {label} <span className="text-[9px]">▾</span>
      </button>
      {open && (
        <div className={`absolute top-[110%] left-0 z-[999] bg-white border border-gray-300 rounded-md shadow-lg ${width} py-1 max-h-72 overflow-y-auto`}>
          {typeof children === "function" ? children(() => setOpen(false)) : children}
        </div>
      )}
    </div>
  );
}

// ── Find & Replace panel (operates across the full combined document) ──────
function FindReplace({ getHtml, setHtml, onClose, afterChange }) {
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [matchCount, setMatchCount] = useState(null);

  const doFind = () => {
    if (!find) return;
    const html = getHtml();
    const re = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const matches = html.match(re);
    setMatchCount(matches ? matches.length : 0);
  };

  const doReplaceAll = () => {
    if (!find) return;
    const html = getHtml();
    const re = new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    setHtml(html.replace(re, replace));
    afterChange();
    setTimeout(doFind, 100);
  };

  return (
    <div className="absolute top-2 right-2 z-[500] bg-white border border-gray-300 rounded-lg shadow-xl p-3 w-72">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[12px] font-bold text-gray-700">Find & Replace</span>
        <button onMouseDown={(e) => { e.preventDefault(); onClose(); }} className="text-gray-400 hover:text-gray-700 cursor-pointer text-xs">✕</button>
      </div>
      <input
        value={find}
        onChange={(e) => setFind(e.target.value)}
        placeholder="Find..."
        className="w-full border border-gray-300 rounded px-2 py-1 text-[12px] mb-1.5"
      />
      <input
        value={replace}
        onChange={(e) => setReplace(e.target.value)}
        placeholder="Replace with..."
        className="w-full border border-gray-300 rounded px-2 py-1 text-[12px] mb-2"
      />
      <div className="flex gap-1.5">
        <button onMouseDown={(e) => { e.preventDefault(); doFind(); }} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-1 text-[11px] font-semibold cursor-pointer">Find</button>
        <button onMouseDown={(e) => { e.preventDefault(); doReplaceAll(); }} className="flex-1 bg-[#2b579a] hover:bg-[#234a85] text-white rounded py-1 text-[11px] font-semibold cursor-pointer">Replace All</button>
      </div>
      {matchCount !== null && <p className="text-[10px] text-gray-500 mt-1.5">{matchCount} match{matchCount === 1 ? "" : "es"} found</p>}
    </div>
  );
}

// ── Main App ────────────────────────────────────────────────────────────────
export default function WordEditor({
  isOpen = true,
  onClose = null,
  isModal = false,
  initialHtml = "",
  onSave = null,
  saveButtonLabel = "Save",
  onChange = null,
  pageSize = "trade",
  margins = "normal",
  theme = "cream",
  pageBorder = "none",
  headerText = "",
  footerText = "",
  showHeaderFooter = false,
}) {
  const pageRefs = useRef([]);
  const activePageIndexRef = useRef(0);
  const measureRef = useRef(null);
  const debounceTimer = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const [fileName, setFileName] = useState("Untitled Document");
  const [hasDoc, setHasDoc] = useState(false);
  const [pages, setPages] = useState([]); // array of HTML strings, one per page
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [fontColor, setFontColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffff00");
  const [currentFont, setCurrentFont] = useState("Arial");
  const [currentSize, setCurrentSize] = useState(12);
  const [activeFormats, setActiveFormats] = useState({});
  const [savedRange, setSavedRange] = useState(null);
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState("");
  const [spellcheckOn, setSpellcheckOn] = useState(true);
  const [formatPainter, setFormatPainter] = useState(null);

  // ── Pagination geometry helpers ───────────────────────────────────────────
  const getDims = useCallback(() => {
    const dim = PAGE_SIZE_MAP[pageSize] || PAGE_SIZE_MAP.trade;
    const marginIn = MARGIN_IN_MAP[margins] ?? 0.8;
    return {
      widthPx: dim.width * PX_PER_IN,
      heightPx: dim.height * PX_PER_IN,
      marginPx: marginIn * PX_PER_IN,
      marginIn,
      dim,
    };
  }, [pageSize, margins]);

  const getAvailableContentHeight = useCallback(() => {
    const { heightPx, marginPx } = getDims();
    const decor = DECOR_EXTRA_PX[pageBorder] || DECOR_EXTRA_PX.none;
    const headerExtra = showHeaderFooter && headerText && !TEMPLATE_HAS_OWN_HEADER.includes(pageBorder) ? 22 : 0;
    const footerExtra = showHeaderFooter && !TEMPLATE_HAS_OWN_FOOTER.includes(pageBorder) ? 22 : 0;
    return Math.max(120, heightPx - marginPx * 2 - decor.top - decor.bottom - headerExtra - footerExtra);
  }, [getDims, pageBorder, showHeaderFooter, headerText]);

  const getContentWidth = useCallback(() => {
    const { widthPx, marginPx } = getDims();
    const decor = DECOR_EXTRA_PX[pageBorder] || DECOR_EXTRA_PX.none;
    return Math.max(100, widthPx - marginPx * 2 - decor.left);
  }, [getDims, pageBorder]);

  // ── Core repagination: splits combined HTML into page-sized chunks ───────
  const repaginate = useCallback((combinedHtmlParam) => {
    if (!measureRef.current) return;
    const combined = combinedHtmlParam !== undefined
      ? combinedHtmlParam
      : pageRefs.current.filter(Boolean).map((el) => el.innerHTML).join("");

    const contentWidth = getContentWidth();
    const availableHeight = getAvailableContentHeight();

    measureRef.current.style.width = `${contentWidth}px`;
    measureRef.current.innerHTML = combined && combined.trim() ? combined : "<p><br></p>";

    const nodes = Array.from(measureRef.current.children);
    const newPages = [];
    let currentNodes = [];
    let currentHeight = 0;

    const pushPage = () => {
      const html = currentNodes.map((n) => n.outerHTML).join("");
      newPages.push(html || "<p><br></p>");
      currentNodes = [];
      currentHeight = 0;
    };

    if (nodes.length === 0) {
      newPages.push("<p><br></p>");
    } else {
      nodes.forEach((node) => {
        const isForcedBreak = node.getAttribute && node.getAttribute("data-page-break") === "true";
        if (isForcedBreak) {
          pushPage();
          return;
        }
        const h = node.offsetHeight + 6;
        if (currentHeight + h > availableHeight && currentNodes.length > 0) {
          pushPage();
        }
        currentNodes.push(node);
        currentHeight += h;
      });
      pushPage();
    }

    if (newPages.length === 0) newPages.push("<p><br></p>");
    setPages(newPages);
  }, [getContentWidth, getAvailableContentHeight]);

  const scheduleRepaginate = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => repaginate(), 900);
  }, [repaginate]);

  // Imperatively sync DOM innerHTML whenever the committed `pages` array changes
  // (initial load, file open, repagination) — never during live typing itself,
  // so the caret is left undisturbed while the user is actively writing.
  useEffect(() => {
    pages.forEach((html, i) => {
      const el = pageRefs.current[i];
      if (el && el.innerHTML !== html) el.innerHTML = html;
    });
  }, [pages]);

  // Sync with initialHtml changes
  useEffect(() => {
    if (isOpen) {
      if (initialHtml) {
        setHasDoc(true);
        setPages([initialHtml]);
        setTimeout(() => repaginate(initialHtml), 120);
      } else if (!isModal) {
        startNew();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialHtml, isModal]);

  // Re-flow content whenever page geometry / header / footer / border settings change
  useEffect(() => {
    if (pages.length > 0) {
      const combined = pages.join("");
      const t = setTimeout(() => repaginate(combined), 80);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, margins, pageBorder, showHeaderFooter, headerText, footerText]);

  // Track active formats on selection change
  useEffect(() => {
    const update = () => {
      const sel = window.getSelection();
      const idx = activePageIndexRef.current;
      const activeEl = pageRefs.current[idx];
      if (!sel || !activeEl || !activeEl.contains(sel.anchorNode)) return;
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikeThrough: document.queryCommandState("strikeThrough"),
        subscript: document.queryCommandState("subscript"),
        superscript: document.queryCommandState("superscript"),
        justifyLeft: document.queryCommandState("justifyLeft"),
        justifyCenter: document.queryCommandState("justifyCenter"),
        justifyRight: document.queryCommandState("justifyRight"),
        justifyFull: document.queryCommandState("justifyFull"),
        insertOrderedList: document.queryCommandState("insertOrderedList"),
        insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      });
      const font = document.queryCommandValue("fontName").replace(/['"]/g, "");
      const size = document.queryCommandValue("fontSize");
      if (font) setCurrentFont(font);
      if (size) setCurrentSize(parseInt([8, 9, 10, 11, 12, 14, 18, 24, 36][size - 1] || 12));
    };
    document.addEventListener("selectionchange", update);
    return () => document.removeEventListener("selectionchange", update);
  }, []);

  // Keyboard shortcuts (Ctrl+B/I/U, Ctrl+S, Ctrl+F, Ctrl+Z/Y)
  useEffect(() => {
    const handler = (e) => {
      const idx = activePageIndexRef.current;
      const activeEl = pageRefs.current[idx];
      const isInEditor = activeEl && (activeEl.contains(document.activeElement) || activeEl === document.activeElement);
      if (!isInEditor) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "b") { e.preventDefault(); exec("bold"); }
      else if (mod && e.key.toLowerCase() === "i") { e.preventDefault(); exec("italic"); }
      else if (mod && e.key.toLowerCase() === "u") { e.preventDefault(); exec("underline"); }
      else if (mod && e.key.toLowerCase() === "f") { e.preventDefault(); setShowFindReplace(true); }
      else if (mod && e.key.toLowerCase() === "s") { e.preventDefault(); handleSave(); }
      else if (mod && e.key.toLowerCase() === "z") { e.preventDefault(); exec("undo"); }
      else if (mod && e.key.toLowerCase() === "y") { e.preventDefault(); exec("redo"); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasDoc) return;
    const t = setTimeout(() => setAutosaveStatus(""), 1500);
    return () => clearTimeout(t);
  }, [wordCount, hasDoc]);

  // ── Counts + change propagation + reflow scheduling ───────────────────────
  const updateCounts = () => {
    const text = pageRefs.current.filter(Boolean).map((el) => el.innerText).join(" ");
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    setWordCount(words);
    setCharCount(text.length);
    if (onChange) {
      const combined = pageRefs.current.filter(Boolean).map((el) => el.innerHTML).join("");
      onChange(combined);
    }
    setAutosaveStatus("Editing…");
    scheduleRepaginate();
  };

  const exec = useCallback((cmd, value = null) => {
    const idx = activePageIndexRef.current;
    const el = pageRefs.current[idx] || pageRefs.current[0];
    el?.focus();
    document.execCommand(cmd, false, value);
    updateCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) setSavedRange(sel.getRangeAt(0).cloneRange());
  };

  const restoreRange = () => {
    if (!savedRange) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange);
  };

  const applyFontColor = (color) => {
    setFontColor(color);
    restoreRange();
    const idx = activePageIndexRef.current;
    pageRefs.current[idx]?.focus();
    document.execCommand("foreColor", false, color);
    updateCounts();
  };

  const applyBgColor = (color) => {
    setBgColor(color);
    restoreRange();
    const idx = activePageIndexRef.current;
    pageRefs.current[idx]?.focus();
    document.execCommand("hiliteColor", false, color);
    updateCounts();
  };

  const applyFont = (font) => { setCurrentFont(font); exec("fontName", font); };

  const applySize = (size) => {
    setCurrentSize(size);
    const map = [[9, 1], [10, 2], [12, 3], [14, 4], [18, 5], [24, 6], [36, 7]];
    const closest = map.reduce((a, b) => Math.abs(b[0] - size) < Math.abs(a[0] - size) ? b : a);
    exec("fontSize", closest[1]);
  };

  const applyParagraphStyle = (styleObj) => {
    const idx = activePageIndexRef.current;
    pageRefs.current[idx]?.focus();
    document.execCommand("formatBlock", false, `<${styleObj.tag}>`);
    const sel = window.getSelection();
    if (sel && sel.anchorNode) {
      let node = sel.anchorNode.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
      const root = pageRefs.current[idx];
      while (node && node !== root && node.tagName !== styleObj.tag) node = node.parentElement;
      if (node && node.tagName === styleObj.tag) node.setAttribute("style", styleObj.style);
    }
    updateCounts();
  };

  const applyLineHeight = (value) => {
    const idx = activePageIndexRef.current;
    const root = pageRefs.current[idx];
    root?.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node = sel.anchorNode.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
    while (node && node !== root && !["P", "DIV", "LI", "H1", "H2", "H3", "H4", "BLOCKQUOTE"].includes(node.tagName)) {
      node = node.parentElement;
    }
    if (node && node !== root) node.style.lineHeight = value;
    updateCounts();
  };

  const applyTextCase = (mode) => {
    const idx = activePageIndexRef.current;
    pageRefs.current[idx]?.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.toString().length === 0) return;
    const text = sel.toString();
    let newText = text;
    if (mode === "upper") newText = text.toUpperCase();
    else if (mode === "lower") newText = text.toLowerCase();
    else if (mode === "title") newText = text.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());
    else if (mode === "sentence") newText = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    document.execCommand("insertText", false, newText);
    updateCounts();
  };

  const clearFormatting = () => exec("removeFormat");
  const applyIndent = (dir) => exec(dir === "in" ? "indent" : "outdent");
  const insertHorizontalRule = () => exec("insertHorizontalRule");

  const insertPageBreak = () => {
    exec("insertHTML", `<div data-page-break="true" style="height:0;"></div>`);
  };

  const insertDate = () => {
    const d = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    exec("insertText", d);
  };

  const insertSpecialChar = (ch) => exec("insertText", ch);

  const copyFormat = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let node = sel.anchorNode.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
    const cs = window.getComputedStyle(node);
    setFormatPainter({
      fontFamily: cs.fontFamily,
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      fontStyle: cs.fontStyle,
      color: cs.color,
      textDecoration: cs.textDecoration,
    });
  };
  const applyPaintedFormat = () => {
    if (!formatPainter) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.toString().length === 0) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    Object.assign(span.style, formatPainter);
    range.surroundContents(span);
    setFormatPainter(null);
    updateCounts();
  };

  const startNew = () => {
    setFileName("Untitled Document");
    setHasDoc(true);
    const initial = `<p style="font-family:Arial;font-size:12pt;">Start typing your document here...</p>`;
    pageRefs.current = [];
    setPages([initial]);
    setTimeout(() => {
      repaginate(initial);
      pageRefs.current[0]?.focus();
    }, 80);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setHasDoc(true);
    pageRefs.current = [];

    const loadInto = (html) => {
      setPages([html]);
      setTimeout(() => repaginate(html), 120);
    };

    if (file.name.endsWith(".docx")) {
      try {
        const mammoth = await loadMammoth();
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        loadInto(result.value || "<p>Could not parse document content.</p>");
      } catch (err) {
        loadInto(`<p style="color:#dc2626;">Could not parse .docx file: ${err.message}</p><p>Please try a plain text or HTML file.</p>`);
      }
    } else if (file.name.endsWith(".txt")) {
      const text = await file.text();
      loadInto(text.split("\n").map((l) => `<p>${l || "<br>"}</p>`).join(""));
    } else if (file.name.endsWith(".html") || file.name.endsWith(".htm")) {
      const text = await file.text();
      loadInto(text);
    } else {
      loadInto(`<p style="color:#6b7280;">File type not fully supported for preview. Showing as plain text:</p><p>${file.name}</p>`);
    }
  };

  const loadMammoth = () => {
    return new Promise((resolve, reject) => {
      if (window.mammoth) return resolve(window.mammoth);
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
      script.onload = () => resolve(window.mammoth);
      script.onerror = () => reject(new Error("Failed to load mammoth.js"));
      document.head.appendChild(script);
    });
  };

  const getCombinedHtml = () => pages.join("");

  const downloadHTML = () => {
    const content = getCombinedHtml();
    const blob = new Blob([`<!DOCTYPE html><html><body>${content}</body></html>`], { type: "text/html" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = fileName.replace(/\.[^.]+$/, "") + ".html"; a.click();
  };

  const downloadTxt = () => {
    const text = pageRefs.current.filter(Boolean).map((el) => el.innerText).join("\n\n");
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = fileName.replace(/\.[^.]+$/, "") + ".txt"; a.click();
  };

  const downloadAsWord = () => {
    const content = getCombinedHtml();
    const html = `<!DOCTYPE html><html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>${fileName}</title></head><body>${content}</body></html>`;
    const blob = new Blob(["\ufeff", html], { type: "application/msword" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = fileName.replace(/\.[^.]+$/, "") + ".doc"; a.click();
  };

  const handleSave = () => {
    const html = getCombinedHtml();
    if (onSave) onSave(html, fileName);
    else downloadHTML();
    setAutosaveStatus("Saved ✓");
  };

  const insertTable = () => {
    const rows = parseInt(prompt("Number of rows:", "3") || "3");
    const cols = parseInt(prompt("Number of columns:", "3") || "3");
    if (!rows || !cols) return;
    const cellStyle = `border:1px solid #d1d5db;padding:6px 10px;min-width:60px;`;
    let html = `<table style="border-collapse:collapse;width:100%;margin:8px 0;">`;
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) {
        html += r === 0
          ? `<th style="${cellStyle}background:#f3f4f6;font-weight:600;">Header</th>`
          : `<td style="${cellStyle}">Cell</td>`;
      }
      html += "</tr>";
    }
    html += "</table><p></p>";
    exec("insertHTML", html);
  };

  const insertLink = () => {
    const url = prompt("Enter URL:", "https://");
    if (url) exec("createLink", url);
  };

  const insertImageFromUrl = () => {
    const url = prompt("Enter image URL:", "https://");
    if (url) exec("insertImage", url);
  };

  const insertImageFromFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressedDataUrl = await compressImage(file, 800, 800, 0.6);
      exec("insertHTML", `<img src="${compressedDataUrl}" style="max-width:100%;height:auto;border-radius:4px;" />`);
    } catch (err) {
      console.error("Error compressing image:", err);
      // Fallback to reading as is if compression fails
      const reader = new FileReader();
      reader.onload = () => {
        exec("insertHTML", `<img src="${reader.result}" style="max-width:100%;height:auto;" />`);
      };
      reader.readAsDataURL(file);
    }
  };

  // Real, literal page-by-page print/PDF output (each page is its own printed page)
  const printDoc = () => {
    const win = window.open("", "_blank");
    const { marginIn } = getDims();
    const pageHtml = pages.map((html, i) => `
      <div class="print-page">
        ${showHeaderFooter && headerText && !TEMPLATE_HAS_OWN_HEADER.includes(pageBorder) ? `<div class="print-header">${headerText}</div>` : ""}
        <div class="print-body">${html}</div>
        ${showHeaderFooter ? `<div class="print-footer"><span>${footerText || ""}</span><span>Page ${i + 1} of ${pages.length}</span></div>` : ""}
      </div>
    `).join("");
    win.document.write(`<!DOCTYPE html><html><head><title>${fileName}</title>
      <style>
        @page { margin: ${marginIn}in; }
        body{font-family:Arial;margin:0;color:#1a1a1a;}
        .print-page{page-break-after:always;position:relative;min-height:85vh;}
        .print-page:last-child{page-break-after:auto;}
        .print-header{font-size:10px;color:#888;text-align:center;margin-bottom:10px;font-style:italic;border-bottom:1px solid #ddd;padding-bottom:4px;}
        .print-footer{font-size:9px;color:#888;display:flex;justify-content:space-between;margin-top:14px;border-top:1px solid #ddd;padding-top:4px;}
        table{border-collapse:collapse;}
      </style></head><body>${pageHtml}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 200);
  };

  const toggleSpellcheck = () => setSpellcheckOn((s) => !s);

  if (!isOpen) return null;

  const themeColors = THEME_MAP[theme] || THEME_MAP.cream;
  const { dim } = getDims();
  const sheetPadding = `${MARGIN_IN_MAP[margins] ?? 0.8}in`;
  const decor = DECOR_EXTRA_PX[pageBorder] || DECOR_EXTRA_PX.none;

  const getSheetStyle = () => ({
    width: "100%",
    maxWidth: `${dim.width}in`,
    minHeight: `${dim.height}in`,
    background: themeColors.bg,
    color: themeColors.text,
    padding: sheetPadding,
    paddingTop: `calc(${sheetPadding} + ${decor.top}px)`,
    paddingBottom: `calc(${sheetPadding} + ${decor.bottom}px)`,
    paddingLeft: `calc(${sheetPadding} + ${decor.left}px)`,
    boxShadow: "0 2px 12px rgba(0,0,0,.18)",
    transform: `scale(${zoom / 100})`,
    transformOrigin: "top center",
    marginBottom: zoom < 100 ? `${(zoom - 100) * 6}px` : "32px",
    transition: "all 0.15s ease-in-out",
    position: "relative",
  });

  const editorLayout = (
    <div className="flex flex-col h-full w-full bg-[#f0f2f5] font-sans">
      {/* Hidden offscreen measurer used purely for pagination math */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: "-9999px",
          visibility: "hidden",
          pointerEvents: "none",
          fontSize: "11pt",
          fontFamily: "Arial",
          lineHeight: 1.4,
        }}
      />

      {/* Title bar */}
      <div className="bg-[#2b579a] text-white py-1.5 px-3 flex items-center gap-3 select-none">
        <span className="text-base">📄</span>
        <input
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          className="bg-transparent border-none text-white text-[13px] font-semibold outline-none flex-1 min-w-0"
        />
        {autosaveStatus && <span className="text-[10px] text-white/70 italic hidden sm:inline">{autosaveStatus}</span>}
        <div className="flex gap-1 ml-auto items-center">
          <button onMouseDown={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}
            className="bg-white/20 hover:bg-white/30 border-none text-white rounded py-1 px-2 cursor-pointer text-[11px] transition-colors duration-150">
            📂 Open
          </button>
          <button onMouseDown={(e) => { e.preventDefault(); startNew(); }}
            className="bg-white/20 hover:bg-white/30 border-none text-white rounded py-1 px-2 cursor-pointer text-[11px] transition-colors duration-150">
            ➕ New
          </button>
          <button onMouseDown={(e) => { e.preventDefault(); printDoc(); }}
            className="bg-white/20 hover:bg-white/30 border-none text-white rounded py-1 px-2 cursor-pointer text-[11px] transition-colors duration-150">
            🖨️ Print
          </button>
          <DropdownMenu label="⬇ Export" title="Download as...">
            {(close) => (
              <>
                <button onMouseDown={(e) => { e.preventDefault(); downloadAsWord(); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">Word (.doc)</button>
                <button onMouseDown={(e) => { e.preventDefault(); downloadHTML(); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">HTML (.html)</button>
                <button onMouseDown={(e) => { e.preventDefault(); downloadTxt(); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">Text (.txt)</button>
                <button onMouseDown={(e) => { e.preventDefault(); printDoc(); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">PDF (via Print)</button>
              </>
            )}
          </DropdownMenu>
          {onSave && (
            <button onMouseDown={(e) => { e.preventDefault(); handleSave(); }}
              className="bg-white hover:bg-slate-100 border-none text-[#2b579a] rounded py-1 px-2 cursor-pointer text-[11px] font-semibold transition-colors duration-150"
              title="Save Document (Ctrl+S)">
              💾 {saveButtonLabel}
            </button>
          )}
          {isModal && onClose && (
            <button onMouseDown={(e) => { e.preventDefault(); onClose(); }}
              className="bg-white/20 hover:bg-white/30 border-none text-white rounded w-6 h-6 cursor-pointer text-xs flex items-center justify-center transition-colors duration-150">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Ribbon toolbar */}
      {hasDoc && (
        <div className="bg-white border-b border-gray-200 py-1.5 px-2 flex flex-wrap gap-[3px] items-center shadow-sm">
          <DropdownMenu label="Styles" title="Paragraph Styles" width="w-48">
            {(close) => PARAGRAPH_STYLES.map((s) => (
              <button
                key={s.label}
                onMouseDown={(e) => { e.preventDefault(); applyParagraphStyle(s); close(); }}
                style={{ fontWeight: s.style.includes("700") ? 700 : 400, fontStyle: s.style.includes("italic") ? "italic" : "normal" }}
                className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-100 cursor-pointer"
              >
                {s.label}
              </button>
            ))}
          </DropdownMenu>

          <Divider />

          <select value={currentFont} onChange={(e) => applyFont(e.target.value)}
            className="border border-gray-300 rounded px-1 py-0.5 text-[11px] min-w-[110px] bg-white">
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>

          <select value={currentSize} onChange={(e) => applySize(parseInt(e.target.value))}
            className="border border-gray-300 rounded px-1 py-0.5 text-[11px] w-12 bg-white">
            {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <Divider />

          <ToolBtn title="Bold (Ctrl+B)" active={activeFormats.bold} onClick={() => exec("bold")}><b>B</b></ToolBtn>
          <ToolBtn title="Italic (Ctrl+I)" active={activeFormats.italic} onClick={() => exec("italic")}><i>I</i></ToolBtn>
          <ToolBtn title="Underline (Ctrl+U)" active={activeFormats.underline} onClick={() => exec("underline")}><u>U</u></ToolBtn>
          <ToolBtn title="Strikethrough" active={activeFormats.strikeThrough} onClick={() => exec("strikeThrough")}><s>S</s></ToolBtn>
          <ToolBtn title="Subscript" active={activeFormats.subscript} onClick={() => exec("subscript")}>X₂</ToolBtn>
          <ToolBtn title="Superscript" active={activeFormats.superscript} onClick={() => exec("superscript")}>X²</ToolBtn>
          <ToolBtn title="Clear Formatting" onClick={clearFormatting}>⌫A</ToolBtn>

          <Divider />

          <div onMouseDown={saveRange}>
            <ColorPicker label="Font Color" icon="A" onColor={applyFontColor} currentColor={fontColor} />
          </div>
          <div onMouseDown={saveRange}>
            <ColorPicker label="Highlight" icon="⬛" onColor={applyBgColor} currentColor={bgColor} />
          </div>

          <Divider />

          <DropdownMenu label="Aa" title="Change Case" width="w-40">
            {(close) => (
              <>
                <button onMouseDown={(e) => { e.preventDefault(); applyTextCase("upper"); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">UPPERCASE</button>
                <button onMouseDown={(e) => { e.preventDefault(); applyTextCase("lower"); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">lowercase</button>
                <button onMouseDown={(e) => { e.preventDefault(); applyTextCase("title"); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">Title Case</button>
                <button onMouseDown={(e) => { e.preventDefault(); applyTextCase("sentence"); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">Sentence case</button>
              </>
            )}
          </DropdownMenu>

          <ToolBtn title="Format Painter (copy then click apply)" active={!!formatPainter} onClick={() => (formatPainter ? applyPaintedFormat() : copyFormat())}>🖌️</ToolBtn>

          <Divider />

          <ToolBtn title="Align Left" active={activeFormats.justifyLeft} onClick={() => exec("justifyLeft")}>⬅</ToolBtn>
          <ToolBtn title="Center" active={activeFormats.justifyCenter} onClick={() => exec("justifyCenter")}>☰</ToolBtn>
          <ToolBtn title="Align Right" active={activeFormats.justifyRight} onClick={() => exec("justifyRight")}>➡</ToolBtn>
          <ToolBtn title="Justify" active={activeFormats.justifyFull} onClick={() => exec("justifyFull")}>≡</ToolBtn>

          <Divider />

          <ToolBtn title="Bullet List" active={activeFormats.insertUnorderedList} onClick={() => exec("insertUnorderedList")}>• List</ToolBtn>
          <ToolBtn title="Numbered List" active={activeFormats.insertOrderedList} onClick={() => exec("insertOrderedList")}>1. List</ToolBtn>
          <ToolBtn title="Decrease Indent" onClick={() => applyIndent("out")}>⇤</ToolBtn>
          <ToolBtn title="Increase Indent" onClick={() => applyIndent("in")}>⇥</ToolBtn>

          <DropdownMenu label="↕ Spacing" title="Line Spacing" width="w-28">
            {(close) => LINE_HEIGHTS.map((lh) => (
              <button key={lh.value} onMouseDown={(e) => { e.preventDefault(); applyLineHeight(lh.value); close(); }} className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-gray-100 cursor-pointer">{lh.label}</button>
            ))}
          </DropdownMenu>

          <Divider />

          <ToolBtn title="Insert Table" onClick={insertTable}>▦ Table</ToolBtn>
          <ToolBtn title="Insert Link" onClick={insertLink}>🔗</ToolBtn>
          <ToolBtn title="Insert Image (URL)" onClick={insertImageFromUrl}>🖼️ URL</ToolBtn>
          <ToolBtn title="Insert Image (Upload)" onClick={() => imageInputRef.current?.click()}>🖼️ Upload</ToolBtn>
          <ToolBtn title="Horizontal Rule" onClick={insertHorizontalRule}>―</ToolBtn>
          <ToolBtn title="Manual Page Break" onClick={insertPageBreak}>⤓ Break</ToolBtn>
          <ToolBtn title="Insert Date" onClick={insertDate}>📅</ToolBtn>

          <DropdownMenu label="Ω Symbol" title="Special Characters" width="w-56">
            {(close) => (
              <div className="grid grid-cols-7 gap-1 p-2">
                {SPECIAL_CHARS.map((ch) => (
                  <button key={ch} onMouseDown={(e) => { e.preventDefault(); insertSpecialChar(ch); close(); }} className="text-[14px] hover:bg-gray-100 rounded py-1 cursor-pointer">{ch}</button>
                ))}
              </div>
            )}
          </DropdownMenu>

          <Divider />

          <ToolBtn title="Find & Replace (Ctrl+F)" onClick={() => setShowFindReplace((s) => !s)}>🔍 Find</ToolBtn>
          <ToolBtn title="Toggle Spellcheck" active={spellcheckOn} onClick={toggleSpellcheck}>ABC✓</ToolBtn>

          <Divider />

          <ToolBtn title="Undo (Ctrl+Z)" onClick={() => exec("undo")}>↩</ToolBtn>
          <ToolBtn title="Redo (Ctrl+Y)" onClick={() => exec("redo")}>↪</ToolBtn>

          <Divider />

          <div className="flex items-center gap-1 ml-auto">
            <ToolBtn title="Zoom Out" onClick={() => setZoom((z) => Math.max(50, z - 10))}>−</ToolBtn>
            <span className="text-[11px] text-gray-600 w-10 text-center">{zoom}%</span>
            <ToolBtn title="Zoom In" onClick={() => setZoom((z) => Math.min(200, z + 10))}>+</ToolBtn>
            <ToolBtn title="Reset Zoom" onClick={() => setZoom(100)}>⟲</ToolBtn>
          </div>
        </div>
      )}

      {/* Main scroll area: each page renders as its own literal sheet, in sequence */}
      <div className="flex-1 overflow-y-auto bg-[#e9ebee] flex flex-col items-center py-6 relative">
        {showFindReplace && (
          <FindReplace
            getHtml={getCombinedHtml}
            setHtml={(newHtml) => { setPages([newHtml]); setTimeout(() => repaginate(newHtml), 80); }}
            onClose={() => setShowFindReplace(false)}
            afterChange={updateCounts}
          />
        )}

        {!hasDoc ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-4 p-5">
            <div className="text-5xl">📄</div>
            <h2 className="m-0 text-lg text-[#1e3a5f] font-bold">Word Editor</h2>
            <div className="flex gap-2">
              <button onClick={() => fileInputRef.current?.click()}
                className="bg-[#2b579a] hover:bg-[#2b579a]/95 text-white border-none rounded py-2 px-4 text-xs cursor-pointer font-semibold transition-colors duration-150">
                📂 Open File
              </button>
              <button onClick={startNew}
                className="bg-white hover:bg-slate-50 text-[#2b579a] border-2 border-[#2b579a] rounded py-1.5 px-4 text-xs cursor-pointer font-semibold transition-colors duration-150">
                ➕ New
              </button>
            </div>
          </div>
        ) : (
          pages.map((_, i) => (
            <div key={i} style={getSheetStyle()}>
              <PageBorderDecor
                styleId={pageBorder}
                themeColors={themeColors}
                headerText={headerText}
                footerText={footerText}
                pageNumber={i + 1}
              />

              {showHeaderFooter && headerText && !TEMPLATE_HAS_OWN_HEADER.includes(pageBorder) && (
                <div className="absolute left-0 right-0 text-center text-[10px] text-gray-400 italic px-5" style={{ top: 8 }}>
                  {headerText}
                </div>
              )}

              <div
                ref={(el) => (pageRefs.current[i] = el)}
                contentEditable
                suppressContentEditableWarning
                onFocus={() => (activePageIndexRef.current = i)}
                onInput={updateCounts}
                onKeyDown={(e) => {
                  if (e.key === "Tab") { e.preventDefault(); exec("insertHTML", "&nbsp;&nbsp;&nbsp;&nbsp;"); }
                }}
                style={{
                  outline: "none",
                  minHeight: "1.5in",
                  fontSize: "11pt",
                  fontFamily: "Arial",
                  lineHeight: 1.4,
                  color: "inherit",
                  caretColor: "#2b579a",
                  position: "relative",
                  zIndex: 1,
                }}
                spellCheck={spellcheckOn}
              />

              {showHeaderFooter && (
                <div className="absolute left-0 right-0 flex justify-between px-5 text-[9px] text-gray-400" style={{ bottom: 8 }}>
                  <span className="italic">{!TEMPLATE_HAS_OWN_FOOTER.includes(pageBorder) ? footerText : ""}</span>
                  <span>{!TEMPLATE_HAS_OWN_FOOTER.includes(pageBorder) ? `Page ${i + 1} of ${pages.length}` : ""}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Status bar */}
      {hasDoc && (
        <div className="bg-[#2b579a] text-white/85 py-0.5 px-3 flex justify-between items-center text-[11px] select-none">
          <span>{fileName}</span>
          <span className="flex gap-3">
            <span>{wordCount} words</span>
            <span>{charCount} characters</span>
            <span>{pages.length} page{pages.length > 1 ? "s" : ""}</span>
          </span>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept=".docx,.txt,.html,.htm" style={{ display: "none" }} onChange={handleFileUpload} />
      <input ref={imageInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={insertImageFromFile} />
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col h-[90vh]">
          {editorLayout}
        </div>
      </div>
    );
  }

  return editorLayout;
}